import React, { useState } from "react";

/**
 * Componente Alert per mostrare messaggi di notifica
 * @param {Object} props - Proprietà del componente
 * @param {string} props.type - Tipo di alert ('success', 'error', 'warning', 'info')
 * @param {string} props.message - Messaggio da mostrare
 * @param {function} props.onClose - Funzione chiamata quando l'alert viene chiuso
 * @param {boolean} props.dismissible - Se l'alert può essere chiuso dall'utente
 * @param {number} props.autoCloseMs - Millisecondi dopo i quali l'alert si chiude automaticamente
 * @returns {JSX.Element|null}
 */
const Alert = ({ 
  type = "info", 
  message, 
  onClose,
  dismissible = true,
  autoCloseMs = 0
}) => {
  const [isVisible, setIsVisible] = useState(true);
  
  // Tipo di alert e impostazioni corrispondenti
  const alertTypes = {
    success: {
      bgColor: "bg-green-100",
      textColor: "text-green-800",
      borderColor: "border-green-200",
      icon: "fa-check-circle"
    },
    error: {
      bgColor: "bg-red-100",
      textColor: "text-red-800",
      borderColor: "border-red-200",
      icon: "fa-times-circle"
    },
    warning: {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
      borderColor: "border-yellow-200",
      icon: "fa-exclamation-triangle"
    },
    info: {
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
      borderColor: "border-blue-200",
      icon: "fa-info-circle"
    }
  };
  
  // Configurazione basata sul tipo
  const alertConfig = alertTypes[type] || alertTypes.info;
  
  // Auto-chiusura dell'alert
  React.useEffect(() => {
    let timer;
    if (autoCloseMs > 0) {
      timer = setTimeout(() => {
        handleClose();
      }, autoCloseMs);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [autoCloseMs]);
  
  // Gestione della chiusura
  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };
  
  if (!isVisible) return null;
  
  return (
    <div className={`${alertConfig.bgColor} ${alertConfig.borderColor} border ${alertConfig.textColor} px-4 py-3 rounded relative mb-4`} role="alert">
      <div className="flex items-start">
        <div className="py-1">
          <i className={`fas ${alertConfig.icon} mr-2`}></i>
        </div>
        <div className="flex-grow">
          {message}
        </div>
        {dismissible && (
          <button
            type="button"
            className={`${alertConfig.textColor} ml-auto -mx-1.5 -my-1.5 p-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg`}
            onClick={handleClose}
            aria-label="Chiudi"
          >
            <span className="sr-only">Chiudi</span>
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;
